import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Card,
    CardContent,
    Chip,
    Button,
    Stack,
    Fade,
    Divider,
    Dialog,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SpeedIcon from '@mui/icons-material/Speed';
import MemoryIcon from '@mui/icons-material/Memory';
import CableIcon from '@mui/icons-material/Cable';
import PowerIcon from '@mui/icons-material/Power';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RequestQuoteModal from './Components/ClearBox/RequestQuoteModal';
import { getActiveClearBoxConfig } from '../../services/api';

// CSS Variables from App.css
const PRIMARY_COLOR = '#6C98AD';
const SECONDARY_COLOR = '#24272a';
const BORDER_RADIUS = '1rem';
const LIGHT_BG = '#f8f9fa';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

// Q&A Content Data
const qaContent = [
    {
        id: 'how-it-works',
        question: 'How does the ClearBox work?',
        answer: `The ClearBox is a make and model agnostic monitoring device that provides visibility into LPBF build execution by recording the same scanner head signals used to drive the galvanometer system, acquiring pedigree data in addition to any system’s in-situ monitoring. It is installed inline between the scanner head and the scanner control hardware, allowing it to observe the commanded scanner positions and the measured encoder based actual positions without altering the machine toolpath. In parallel, the ClearBox records key laser signals including an analog 0 to 10 V laser power signal and the digital laser modulation signal that indicates laser on and off state, enabling synchronized, layer wise build execution records at a 10 microsecond time step, equivalent to a 100 kHz data stream.
        
        All captured signals are stored in a Lossless Build Log using the HDF5 hierarchical data format. The Lossless Build Log consolidates raw build execution data, calibration information, build platform positioning, and optional sensor channels into a single, portable file that can be opened in the Lossless Build Log Visualizer for review and analysis across layers and over time. The visualizer supports inspection of laser on/off state, speed, and power, and it includes plugin support for custom analysis.
        `},
    {
        id: 'what-it-captures',
        question: 'What information can the ClearBox capture?',
        answer: `The ClearBox captures galvanometer commanded position and actual encoder position signals, as well as laser power and laser state signals. It also supports up to five analog sensor inputs to record additional machine state parameters such as build chamber oxygen, pressure, and temperature, when those sensors are available and connected. The Lossless Build Log structure is designed to store these signals alongside build platform positioning and machine calibration data, enabling a consistent record of build execution and machine configuration for later review and comparison.`
    },
    {
        id: 'how-captured',
        question: 'How is that information captured?',
        answer: `The ClearBox captures scanner data through either SL2-100 or XY2-100E control interface protocols, which can provide 16-bit commanded position and 20-bit actual encoder position at a 100 kHz rate. Laser power is captured through an analog input, and laser on/off behavior is captured through the digital modulation signal. All module outputs are archived in the Lossless Build Log HDF5 file, with sensor values converted to volts for consistent storage.`
    },
    {
        id: 'connection',
        question: 'How is the ClearBox connected to the machine?',
        answer: `The ClearBox is installed inline on the scanner head interface path, between the scanner head and the scanner control hardware, so it can observe the commanded and actual galvanometer signals used during build execution. It also connects to the laser power signal and to the digital laser modulation signal to capture synchronized power and laser on/off state information. For additional monitoring, it provides ports for analog sensor inputs. Installation typically includes a machine-specific configuration and calibration step so the recorded bits can be converted into engineering units and aligned to the build coordinate system, including scan field correction and volt to watt conversion.`},
    {
        id: 'machine-changes',
        question: 'What changes are required on the machine?',
        answer: `ClearBox is installed in series with the scanner system and operates as a pass through device during normal operation while monitoring the bus. No permanent mechanical modifications are typically required beyond installing the provided 12 V power supply and jumper connections. `},
    {
        id: 'reversibility',
        question: 'Can systems be returned to their original configuration?',
        answer: `The system can be returned to the original state by removing the ClearBox inline scanhead cable, removing laser splitters or adapters, and removing power supply jumpers. `
    }
];

// Installation Requirements Data
const installationRequirements = [
    {
        icon: <AspectRatioIcon sx={{ fontSize: 32, color: '#ffffff' }} />,
        title: 'Physical Space',
        specs: [
            <>ClearBox enclosure is <strong>8.25 in by 7.88 in by 1.725 in</strong> and is sized to match a <em>half rack chassis footprint</em>.</>,
            <>Maintain at least <strong>2 inches of clearance</strong> at the front and rear panels for cable bend radius and connector access.</>,
            <>Typical placement is <strong>on top of the machine</strong>, with options for <em>half rack mounting</em> or <em>DIN rail mounting</em> depending on the installation approach.</>
        ]
    },
    {
        icon: <PowerIcon sx={{ fontSize: 32, color: '#ffffff' }} />,
        title: 'Power Supply',
        specs: [
            <>A DIN rail mounted <strong>12 V, 3 or 5 A</strong> power supply is provided and can be powered from any <strong>100 to 240 VAC</strong> source within the machine.</>,
            <>The <em>negative DC terminal</em> must be referenced to the <strong>machine DC negative rail</strong>, typically using a short jumper to a known negative reference point.</>
        ]
    },
    {
        icon: <CableIcon sx={{ fontSize: 32, color: '#ffffff' }} />,
        title: 'Cables & Connections',
        specs: [
            <>Scanner head interface cable compatible with <strong>XY2-100E</strong> or <strong>SL2-100</strong> as applicable.</>,
            <>Laser signal <em>adapters or splitters</em> for <strong>laser state</strong> and <strong>laser power</strong>.</>,
            <><strong>Ethernet cable</strong> between ClearBox and the recording computer.</>
        ]
    }
];

// Compatible Machines
const compatibleMachines = [
    'EOS M290',
    'AMCM M290 FLX',
    'EOS M280',
    'EOS M400',
    'AconityMIDI+',
    'Trumpf TruPrint 3000',
    'Concept Laser M2 Series 5'
];

// Case Studies / Research Publications
const caseStudies = [
    {
        title: 'Predicting porosity in laser powder bed fusion of metals (PBF-LB/M) using scanning data and machine learning',
        link: 'https://www.sciencedirect.com/science/article/pii/S2772369026000101?via%3Dihub',
        source: 'ScienceDirect',
        year: '2026'
    },
    {
        title: 'Scanner position accuracy, repeatability, and process anomalies correlation in PBF-LB/M',
        link: 'https://www.sciencedirect.com/science/article/pii/S2772369025000593',
        source: 'ScienceDirect',
        year: '2025'
    }
];

export default function ClearBoxWorkingPrinciple() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    
    const [expandedAccordion, setExpandedAccordion] = useState('how-it-works');
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [isDiagramOpen, setIsDiagramOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [basePrice, setBasePrice] = useState(9800);
    const heroRef = useRef(null);

    // Fetch ClearBox config for pricing
    useEffect(() => {
        getActiveClearBoxConfig()
            .then((res) => {
                if (res?.data?.basePrice) {
                    setBasePrice(res.data.basePrice);
                }
            })
            .catch((err) => {
                console.error('Could not fetch ClearBox config:', err.message);
            });
    }, []);

    // Format price
    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price);
    };

    // Scroll reveal animation
    useEffect(() => {
        const node = heroRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.2 }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', bgcolor: LIGHT_BG }}>
            <RequestQuoteModal open={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />

            {/* Hero Section */}
            <Box
                ref={heroRef}
                sx={{
                    background: `linear-gradient(135deg, ${SECONDARY_COLOR} 0%, #3a4045 100%)`,
                    color: '#ffffff',
                    minHeight: { xs: '60vh', md: '50vh' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: { xs: 8, md: 10 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                        opacity: 0.5
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Fade in={isVisible} timeout={800}>
                        <Box sx={{ textAlign: 'center' }}>
                            {/* ClearBox Logo */}
                            <Box
                                component="img"
                                src={process.env.PUBLIC_URL + '/static/images/ClearBox/clearbox.svg'}
                                alt="ClearBox logo"
                                sx={{
                                    width: { xs: '220px', sm: '280px', md: '340px' },
                                    maxWidth: '100%',
                                    mb: 3,
                                    filter: 'brightness(0) invert(1)',
                                    opacity: 0.95
                                }}
                            />
                            
                            <Typography
                                variant={isMobile ? 'h4' : 'h3'}
                                component="h1"
                                gutterBottom
                                sx={{
                                    fontWeight: 700,
                                    mb: 3,
                                    fontFamily: "'Avenir Next LT Pro', sans-serif"
                                }}
                            >
                                Working Principle
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.15rem' },
                                    maxWidth: '800px',
                                    mx: 'auto',
                                    lineHeight: 1.8,
                                    opacity: 0.9
                                }}
                            >
                                Technical details on how ClearBox captures signals from LPBF systems, 
                                processes build execution data, and provides comprehensive monitoring 
                                for additive manufacturing processes.
                            </Typography>

                            {/* Key Stats Row */}
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={3}
                                justifyContent="center"
                                sx={{ mt: 5 }}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <SpeedIcon sx={{ fontSize: 36, color: PRIMARY_COLOR, mb: 1 }} />
                                    <Typography variant="h5" fontWeight={700}>100 kHz</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Data Stream</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <MemoryIcon sx={{ fontSize: 36, color: PRIMARY_COLOR, mb: 1 }} />
                                    <Typography variant="h5" fontWeight={700}>10 μs</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Time Resolution</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <CableIcon sx={{ fontSize: 36, color: PRIMARY_COLOR, mb: 1 }} />
                                    <Typography variant="h5" fontWeight={700}>5+</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Analog Inputs</Typography>
                                </Box>
                            </Stack>

                            {/* Price and Lead Time */}
                            <Box
                                sx={{
                                    mt: 5,
                                    pt: 4,
                                    borderTop: '1px solid rgba(255,255,255,0.15)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1.5
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 700,
                                        color: PRIMARY_COLOR,
                                        fontFamily: "'Avenir Next LT Pro', sans-serif"
                                    }}
                                >
                                    Starting at {formatPrice(basePrice)}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        opacity: 0.7,
                                        fontStyle: 'italic',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    Please note the ClearBox has a lead time of 8 weeks
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        opacity: 0.6,
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    *Prices exclude taxes
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/projects/clearbox-buy')}
                                    sx={{
                                        mt: 2,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: '999px',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        textTransform: 'none',
                                        bgcolor: PRIMARY_COLOR,
                                        color: '#fff',
                                        boxShadow: '0 4px 14px rgba(108, 152, 173, 0.4)',
                                        transition: TRANSITION,
                                        '&:hover': {
                                            bgcolor: '#5a8499',
                                            boxShadow: '0 6px 20px rgba(108, 152, 173, 0.5)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    Request a Quote
                                </Button>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* System Overview Diagram Section */}
            <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            textAlign: 'center',
                            color: SECONDARY_COLOR,
                            fontFamily: "'Avenir Next LT Pro', sans-serif"
                        }}
                    >
                        System Overview
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: '#666',
                            mb: 4,
                            maxWidth: '700px',
                            mx: 'auto'
                        }}
                    >
                        ClearBox captures signals from the LPBF system, processes them through dedicated software, 
                        and stores build data in the HDF5 Lossless Build Log for visualization and analysis.
                    </Typography>

                    {/* Clickable Diagram */}
                    <Box
                        onClick={() => setIsDiagramOpen(true)}
                        sx={{
                            position: 'relative',
                            maxWidth: '1000px',
                            mx: 'auto',
                            cursor: 'pointer',
                            borderRadius: BORDER_RADIUS,
                            overflow: 'hidden',
                            boxShadow: CARD_SHADOW,
                            transition: TRANSITION,
                            '&:hover': {
                                boxShadow: CARD_SHADOW_HOVER,
                                transform: 'scale(1.01)',
                                '& .zoom-overlay': {
                                    opacity: 1
                                }
                            }
                        }}
                    >
                        <Box
                            component="img"
                            src={process.env.PUBLIC_URL + '/static/images/ClearBox/clearbox_system_diagram.png'}
                            alt="ClearBox System Architecture Diagram"
                            sx={{
                                width: '100%',
                                height: 'auto',
                                display: 'block'
                            }}
                        />
                        {/* Zoom Overlay */}
                        <Box
                            className="zoom-overlay"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: 'rgba(0,0,0,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: TRANSITION
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    borderRadius: '50px',
                                    px: 3,
                                    py: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <ZoomInIcon sx={{ color: PRIMARY_COLOR }} />
                                <Typography variant="body2" fontWeight={600} color={SECONDARY_COLOR}>
                                    Click to enlarge
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* Fullscreen Diagram Dialog */}
            <Dialog
                open={isDiagramOpen}
                onClose={() => setIsDiagramOpen(false)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        bgcolor: '#ffffff',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        m: 2,
                        borderRadius: BORDER_RADIUS
                    }
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        onClick={() => setIsDiagramOpen(false)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(255,255,255,0.9)',
                            boxShadow: CARD_SHADOW,
                            zIndex: 1,
                            '&:hover': {
                                bgcolor: '#ffffff'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={process.env.PUBLIC_URL + '/static/images/ClearBox/clearbox_system_diagram.png'}
                        alt="ClearBox System Architecture Diagram"
                        sx={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            p: 2
                        }}
                    />
                </Box>
            </Dialog>

            {/* Q&A Section */}
            <Box sx={{ py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 4,
                            textAlign: 'center',
                            color: SECONDARY_COLOR,
                            fontFamily: "'Avenir Next LT Pro', sans-serif"
                        }}
                    >
                        Technical Overview
                    </Typography>

                    <Box sx={{ maxWidth: '900px', mx: 'auto' }}>
                        {qaContent.map((item) => (
                            <Accordion
                                key={item.id}
                                expanded={expandedAccordion === item.id}
                                onChange={handleAccordionChange(item.id)}
                                sx={{
                                    mb: 2,
                                    borderRadius: `${BORDER_RADIUS} !important`,
                                    boxShadow: CARD_SHADOW,
                                    transition: TRANSITION,
                                    '&:hover': {
                                        boxShadow: CARD_SHADOW_HOVER
                                    },
                                    '&::before': { display: 'none' },
                                    '&.Mui-expanded': {
                                        margin: '0 0 16px 0'
                                    }
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: PRIMARY_COLOR }} />}
                                    sx={{
                                        py: 1,
                                        px: 3,
                                        '& .MuiAccordionSummary-content': { my: 2 },
                                        '&.Mui-expanded': {
                                            borderBottom: `1px solid ${PRIMARY_COLOR}20`
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: SECONDARY_COLOR,
                                            fontSize: { xs: '1rem', md: '1.1rem' }
                                        }}
                                    >
                                        {item.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, py: 3 }}>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#555',
                                            lineHeight: 1.8,
                                            whiteSpace: 'pre-line'
                                        }}
                                    >
                                        {item.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* Installation Requirements Section */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            textAlign: 'center',
                            color: SECONDARY_COLOR,
                            fontFamily: "'Avenir Next LT Pro', sans-serif"
                        }}
                    >
                        Installation Requirements
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: '#666',
                            mb: 5,
                            maxWidth: '700px',
                            mx: 'auto'
                        }}
                    >
                        ClearBox is designed for straightforward inline installation with minimal modifications 
                        to existing LPBF systems.
                    </Typography>

                    <Grid container spacing={3}>
                        {installationRequirements.map((req, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        borderRadius: BORDER_RADIUS,
                                        boxShadow: CARD_SHADOW,
                                        transition: TRANSITION,
                                        overflow: 'hidden',
                                        '&:hover': {
                                            boxShadow: CARD_SHADOW_HOVER,
                                            transform: 'translateY(-4px)'
                                        }
                                    }}
                                >
                                    {/* Card Header with Icon */}
                                    <Box
                                        sx={{
                                            bgcolor: PRIMARY_COLOR,
                                            py: 2.5,
                                            px: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2
                                        }}
                                    >
                                        {req.icon}
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 700,
                                                color: '#ffffff',
                                                fontSize: '1.1rem'
                                            }}
                                        >
                                            {req.title}
                                        </Typography>
                                    </Box>
                                    
                                    {/* Specs List */}
                                    <CardContent sx={{ p: 0 }}>
                                        {req.specs.map((statement, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 1.5,
                                                    px: 3,
                                                    py: 2,
                                                    borderBottom: i < req.specs.length - 1 ? '1px solid rgba(108, 152, 173, 0.1)' : 'none',
                                                    transition: TRANSITION,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(108, 152, 173, 0.04)'
                                                    }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 8,
                                                        height: 8,
                                                        borderRadius: '50%',
                                                        bgcolor: PRIMARY_COLOR,
                                                        flexShrink: 0,
                                                        mt: 0.8
                                                    }}
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#444',
                                                        fontSize: '0.9rem',
                                                        lineHeight: 1.7,
                                                        textAlign: 'left'
                                                    }}
                                                >
                                                    {statement}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Compatible Machines Section */}
            <Box sx={{ py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            textAlign: 'center',
                            color: SECONDARY_COLOR,
                            fontFamily: "'Avenir Next LT Pro', sans-serif"
                        }}
                    >
                        Supported LPBF Platforms
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: '#666',
                            mb: 5,
                            maxWidth: '700px',
                            mx: 'auto'
                        }}
                    >
                        ClearBox is designed as a brand agnostic solution and has been installed 
                        and tested on multiple LPBF platforms.
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: 2,
                            maxWidth: '800px',
                            mx: 'auto'
                        }}
                    >
                        {compatibleMachines.map((machine, index) => (
                            <Chip
                                key={index}
                                label={machine}
                                sx={{
                                    px: 3,
                                    py: 3,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    bgcolor: '#ffffff',
                                    border: `2px solid ${PRIMARY_COLOR}`,
                                    color: SECONDARY_COLOR,
                                    borderRadius: BORDER_RADIUS,
                                    boxShadow: CARD_SHADOW,
                                    transition: TRANSITION,
                                    '&:hover': {
                                        bgcolor: PRIMARY_COLOR,
                                        color: '#ffffff',
                                        boxShadow: CARD_SHADOW_HOVER
                                    }
                                }}
                            />
                        ))}
                    </Box>

                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                            color: '#888',
                            mt: 4,
                            fontStyle: 'italic'
                        }}
                    >
                        Contact us for compatibility with other LPBF systems
                    </Typography>
                </Container>
            </Box>

            {/* Case Studies / Research Section */}
            <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: '#ffffff' }}>
                <Container maxWidth="lg">
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            textAlign: 'center',
                            color: SECONDARY_COLOR,
                            fontFamily: "'Avenir Next LT Pro', sans-serif"
                        }}
                    >
                        Validated in Research
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: '#666',
                            mb: 5,
                            maxWidth: '700px',
                            mx: 'auto'
                        }}
                    >
                        ClearBox technology has been used in peer-reviewed studies to investigate porosity prediction, scanner accuracy, and process behavior in laser powder bed fusion.
                    </Typography>

                    <Grid container spacing={3} justifyContent="center">
                        {caseStudies.map((study, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        borderRadius: BORDER_RADIUS,
                                        boxShadow: CARD_SHADOW,
                                        transition: TRANSITION,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            boxShadow: CARD_SHADOW_HOVER,
                                            transform: 'translateY(-4px)'
                                        }
                                    }}
                                >
                                    {/* Card Header */}
                                    <Box
                                        sx={{
                                            bgcolor: PRIMARY_COLOR,
                                            py: 2,
                                            px: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <ArticleIcon sx={{ fontSize: 28, color: '#ffffff' }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#ffffff',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}
                                            >
                                                {study.source}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={study.year}
                                            size="small"
                                            sx={{
                                                bgcolor: 'rgba(255,255,255,0.2)',
                                                color: '#ffffff',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>

                                    {/* Card Content */}
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                color: SECONDARY_COLOR,
                                                fontSize: '1rem',
                                                lineHeight: 1.5,
                                                mb: 2
                                            }}
                                        >
                                            {study.title}
                                        </Typography>
                                    </CardContent>

                                    {/* Card Action */}
                                    <Box sx={{ p: 3, pt: 0 }}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            href={study.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            endIcon={<OpenInNewIcon />}
                                            sx={{
                                                borderColor: PRIMARY_COLOR,
                                                borderWidth: 2,
                                                color: SECONDARY_COLOR,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                borderRadius: BORDER_RADIUS,
                                                py: 1.2,
                                                transition: TRANSITION,
                                                '&:hover': {
                                                    borderColor: PRIMARY_COLOR,
                                                    borderWidth: 2,
                                                    bgcolor: PRIMARY_COLOR,
                                                    color: '#ffffff'
                                                }
                                            }}
                                        >
                                            View Case Study
                                        </Button>
                                    </Box>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Divider />

            {/* CTA Section */}
            <Box
                sx={{
                    py: { xs: 8, md: 10 },
                    bgcolor: '#ffffff',
                    textAlign: 'center'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 2,
                            color: SECONDARY_COLOR,
                            fontFamily: "'Avenir Next LT Pro', sans-serif"
                        }}
                    >
                        Ready to Get Started?
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            mb: 4,
                            maxWidth: '600px',
                            mx: 'auto'
                        }}
                    >
                        Configure your system to see ClearBox pricing and request a quote.
                    </Typography>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2.5}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => navigate('/projects/clearbox-buy')}
                            sx={{
                                px: 5,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: BORDER_RADIUS,
                                borderColor: PRIMARY_COLOR,
                                borderWidth: 2,
                                color: SECONDARY_COLOR,
                                transition: TRANSITION,
                                '&:hover': {
                                    borderColor: PRIMARY_COLOR,
                                    borderWidth: 2,
                                    bgcolor: `${PRIMARY_COLOR}10`
                                }
                            }}
                        >
                            Request a Quote
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
}
